import { QueryKey, UseInfiniteQueryOptions, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query'

/**
 * Type-safe query options helper for React Query v4 (compatible with v5's queryOptions API).
 *
 * Comparison with v5 (https://github.com/TanStack/query/blob/main/packages/query-core/src/queryOptions.ts):
 * - Runtime: Same — v5 is `export function queryOptions(options) { return options }`. We do the same.
 * - Typing: Same goal — wrapping the options in a function forces TypeScript to check the object,
 *   so typos (e.g. `staleTime` vs `stallTime`) are caught. We use UseQueryOptions from v4 for full compatibility.
 * - v5-only: DataTag — in v5, queryKey created via queryOptions is tagged with the queryFn return type,
 *   so `queryClient.getQueryData(options.queryKey)` infers the type. That requires v5 internals; on v4
 *   you can type getQueryData explicitly: `queryClient.getQueryData<MyData>(key)`.
 *
 * Use this to define query option objects that can be passed to useQuery, prefetchQuery, etc.
 *
 * @see https://tkdodo.eu/blog/the-query-options-api
 * @see https://tanstack.com/query/latest/docs/framework/react/reference/queryOptions
 */
export function queryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(obj: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
  return obj
}

/**
 * Same as queryOptions but for useInfiniteQuery option factories (v5 has infiniteQueryOptions).
 * Use when defining infinite query option objects for useInfiniteQuery, prefetchInfiniteQuery, etc.
 */
export function infiniteQueryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(obj: UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey>) {
  return obj
}

/**
 * Type-safe mutation options helper for useMutation (aligned with React Query v5's mutationOptions API).
 * Use this to define mutation option factory functions in the /api folder. Name them *MutationOptions
 * (e.g. createCandidateMutationOptions()). Components use useMutation(createCandidateMutationOptions())
 * so the same options can be reused and typos (e.g. onSucess) are caught.
 */
export function mutationOptions<TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
  obj: UseMutationOptions<TData, TError, TVariables, TContext>
) {
  return obj
}
